import { syllabifyWord } from '../parsers/converter/from_separated_to_compounded';
import { IsClef } from '../parsers/converter/gabcExceptions';
import { split_string } from './helpers';

export interface CursorDef {
  index: number;
  indexType: 'cursor' | 'syllable';
  type: 'lyrics' | 'gabc' | 'nabc' | 'classic';
}

export interface HighlightDef {
  index: number;
  length: number;
}

enum SyllablePosition {
  Beginning,
  Middle,
  End,
  OneWord,
}

interface Syllable {
  syllable: string;
  position: SyllablePosition;
}

export function find_highlight_positions(
  cursor: CursorDef,
  lyrics: string,
  gabc: string,
  nabc?: string,
): {
  'lyrics': HighlightDef,
  'gabc': HighlightDef,
  'nabc': HighlightDef,
  syllableIndex: number,
} {
  const lyricsWords = split_string(lyrics, false);
  const lyricsArr: Syllable[] = [];

  for (const word of lyricsWords) {
    const syllables = syllabifyWord(word, false);

    for (let i = 0; i < syllables.length; i++) {
      const syllable = syllables[i];
      let position = SyllablePosition.Middle;
      if (syllables.length === 1) {
        position = SyllablePosition.OneWord;
      } else if (i === 0) {
        position = SyllablePosition.Beginning;
      } else if (i === syllables.length - 1) {
        position = SyllablePosition.End;
      }
      lyricsArr.push({ syllable, position });
    }
  }

  const gabcArr = split_string(gabc, false);
  const nabcArr = nabc ? split_string(nabc, false) : [];

  if (cursor.type === 'lyrics') {
    const index = cursor.indexType === 'syllable' ?
      cursor.index - 1 : from_absolute_to_jagged_lyrics(lyricsArr, cursor.index);
    const lyricAbsolute = from_jagged_to_absolute_lyrics(lyricsArr, index);
    const gabcAbsolute = from_jagged_to_absolute(gabcArr, index + 1);
    const nabcAbsolute = from_jagged_to_absolute(nabcArr, index);

    return {
      'lyrics': { index: lyricAbsolute, length: lyricsArr[index].syllable.length },
      'gabc': { index: gabcAbsolute, length: gabcArr[index + 1].length },
      'nabc': { index: nabcAbsolute, length: nabcArr[index].length },
      'syllableIndex': index + 1,
    };
  }

  if (cursor.type === 'gabc') {
    const index = cursor.indexType === 'syllable' ?
      cursor.index : from_absolute_to_jagged(gabcArr, cursor.index);

    if (IsClef(gabcArr[index])) {
      const gabcAbsolute = from_jagged_to_absolute(gabcArr, index);

      return {
        'lyrics': { index: 0, length: 0 },
        'gabc': { index: gabcAbsolute, length: gabcArr[index].length },
        'nabc': { index: 0, length: 0 },
        'syllableIndex': index,
      };
    }

    const lyricAbsolute = from_jagged_to_absolute_lyrics(lyricsArr, index - 1);
    const gabcAbsolute = from_jagged_to_absolute(gabcArr, index);
    const nabcAbsolute = from_jagged_to_absolute(nabcArr, index - 1);

    return {
      'lyrics': { index: lyricAbsolute, length: lyricsArr[index - 1].syllable.length },
      'gabc': { index: gabcAbsolute, length: gabcArr[index].length },
      'nabc': { index: nabcAbsolute, length: nabcArr[index - 1].length },
      'syllableIndex': index,
    };
  }

  if (cursor.type === 'nabc') {
    const index = cursor.indexType === 'syllable' ?
      cursor.index - 1 : from_absolute_to_jagged(nabcArr, cursor.index);
    const lyricAbsolute = from_jagged_to_absolute_lyrics(lyricsArr, index);
    const gabcAbsolute = from_jagged_to_absolute(gabcArr, index + 1);
    const nabcAbsolute = from_jagged_to_absolute(nabcArr, index);

    return {
      'lyrics': { index: lyricAbsolute, length: lyricsArr[index].syllable.length },
      'gabc': { index: gabcAbsolute, length: gabcArr[index + 1].length },
      'nabc': { index: nabcAbsolute, length: nabcArr[index].length },
      'syllableIndex': index + 1,
    };
  }

  return {
    'lyrics': { index: 0, length: 0 },
    'gabc': { index: 0, length: 0 },
    'nabc': { index: 0, length: 0 },
    'syllableIndex': -1,
  };
}

function from_jagged_to_absolute(arr: string[], rowIndex: number): number {
  let sum = 0;

  for (let i = 0; i < rowIndex; i++) {
    sum += arr[i].length + 1;
  }

  return sum;
}

function from_absolute_to_jagged(arr: string[], absoluteIndex: number): number {
  let index = 0;
  let sum = arr[0].length;

  while (sum < absoluteIndex && index < arr.length - 1) {
    sum += arr[index + 1].length + 1;
    index++;
  }

  return index;
}

function from_jagged_to_absolute_lyrics(arr: Syllable[], rowIndex: number): number {
  let sum = 0;

  for (let i = 0; i < rowIndex; i++) {
    sum += arr[i].syllable.length;
    if (arr[i].position === SyllablePosition.End || arr[i].position === SyllablePosition.OneWord) {
      sum++;
    }
  }

  return sum;
}

function from_absolute_to_jagged_lyrics(arr: Syllable[], absoluteIndex: number): number {
  let index = 0;
  let sum = arr[0].syllable.length;

  while (sum <= absoluteIndex && index < arr.length - 1) {
    sum += arr[index + 1].syllable.length;
    if (arr[index + 1].position === SyllablePosition.End || arr[index].position === SyllablePosition.OneWord) {
      sum++;
    }
    index++;
  }

  return index;
}
